import type { ConversationListPage, ConversationSort } from "@/api";
import type {
  InboxActiveSection,
  InboxAssignmentFilter,
  InboxDateRange,
  InboxPeriodPreset,
  InboxStatusBucket,
  InboxTaskCounts,
  InboxTaskFilter,
} from "@/screens/screen-inbox.logic";

export interface FocusedWorklistRow {
  /** Server row identity. This object lives only in the current JavaScript tab. */
  id: string;
  index: number;
  section?: InboxTaskFilter;
}

export interface InboxReturnContext {
  kind: "inbox";
  /** Ephemeral copies of the rows that were visible; never written outside this JavaScript tab. */
  conversationPage: ConversationListPage | null;
  sectionPages: Record<InboxActiveSection, ConversationListPage | null>;
  taskCounts: InboxTaskCounts;
  countsKnown?: boolean;
  query: string;
  taskFilter: InboxTaskFilter;
  statusFilter: InboxStatusBucket | "";
  assignment: InboxAssignmentFilter;
  dateRange: InboxDateRange;
  periodPreset: InboxPeriodPreset;
  sort: ConversationSort;
  offset: number;
  sectionOffsets: Record<InboxActiveSection, number>;
  scrollTop: number;
  focusedRow: FocusedWorklistRow | null;
}

export type WorklistReturnContext = InboxReturnContext;

/** Reconcile a confirmed move only; pagination and counts still come from the next server read. */
export function withoutTrashedRequest(page: ConversationListPage | null, requestId: string): ConversationListPage | null {
  if (!page || page.scope === "trash" || !page.items.some((row) => row.target_request_id === requestId)) return page;
  return { ...page, items: page.items.filter((row) => row.target_request_id !== requestId) };
}

function scrollOwners(): HTMLElement[] {
  if (typeof document === "undefined" || typeof document.querySelectorAll !== "function") return [];
  return Array.from(document.querySelectorAll<HTMLElement>(".topshell__scroll, .neo-shell__main, .app-root"));
}

function primaryScrollOwner(owners: HTMLElement[]): HTMLElement | undefined {
  // Follow the layout, including when the page is too short to overflow. On narrow screens
  // these containers have visible overflow and the document owns scrolling instead.
  return owners.find((element) => {
    const overflow = window.getComputedStyle(element).overflowY;
    return overflow === "auto" || overflow === "scroll";
  });
}

/**
 * On a phone the window scrolls; on a desktop the shell's area below the navigation bar does
 * (#2250). Only that layout's scroll owner supplies and receives the remembered position.
 */
export function readPrimaryScrollTop(): number {
  if (typeof window === "undefined") return 0;
  const owner = primaryScrollOwner(scrollOwners());
  return Math.max(0, owner ? owner.scrollTop : window.scrollY || document.documentElement?.scrollTop || 0);
}

export function writePrimaryScrollTop(value: number): void {
  if (typeof window === "undefined") return;
  const top = Math.max(0, Number.isFinite(value) ? value : 0);
  const owners = scrollOwners();
  const owner = primaryScrollOwner(owners);
  // Safari can programmatically scroll the hidden desktop document. Giving it the list's
  // offset moves the entire shell up and exposes a blank strip below it on browser Back.
  for (const element of owners) {
    if (element !== owner) element.scrollTop = 0;
  }
  window.scrollTo({ top: owner ? 0 : top, behavior: "instant" });
  if (owner) owner.scrollTop = top;
}

export function subscribePrimaryScroll(listener: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const owners = ([window, ...scrollOwners()] as unknown[]).filter(
    (owner): owner is EventTarget => {
      const candidate = owner as Partial<EventTarget>;
      return (
        typeof candidate.addEventListener === "function" &&
        typeof candidate.removeEventListener === "function"
      );
    },
  );
  for (const owner of owners) owner.addEventListener("scroll", listener, { passive: true });
  return () => {
    for (const owner of owners) owner.removeEventListener("scroll", listener);
  };
}
